

export interface ChatMessage {
  id: string;
  role: 'user' | 'model';
  text: string;
  timestamp: Date;
  isAdminReply?: boolean;
  isReceipt?: boolean;
  isStepGuide?: boolean;
  imageUrl?: string;
  mediumId?: number;
  mediumName?: string;
  senderAgentId?: string;
  senderAgentName?: string;
  receiptData?: {
    packName: string;
    price: number;
    minutes: number;
    points: number;
    code: string;
    date: string;
  };
}

export interface Review {
    id: string;
    author: string;
    text: string;
    rating: number;
    date: string;
    timestamp?: number; // Added for sorting
    isReal?: boolean; // Pour distinguer les vrais avis des faux générés
    read?: boolean; // Pour les notifications admin
    mediumId?: number; // Pour savoir à quel médium appartient l'avis
}

export type MediumTemperament = 'sensuel' | 'strict' | 'chaleureux' | 'mystique' | 'analytique';
export type MediumDomain = 'Amour' | 'Travail' | 'Finance';

export interface Medium {
  id: number;
  name: string;
  specialties: string[];
  rating: number;
  reviews: number;
  reviewsList: Review[];
  status: 'available' | 'busy' | 'away';
  imageUrl: string;
  description: string;
  experienceYears: number;
  tags: string[];
  temperament?: MediumTemperament;
  isVip?: boolean;
  domain?: MediumDomain;
}

export type LoyaltyTier = 'Bronze' | 'Argent' | 'Or' | 'Platine' | 'Diamant';

export interface LoyaltyReward {
    id: string;
    title: string;
    description: string;
    cost: number;
    icon: string;
    category: 'minutes' | 'tarot' | 'discount' | 'vip';
    badge?: string;
}

export interface LoyaltyPointsTransaction {
    id: string;
    date: string;
    amount: number;
    type: 'session_earned' | 'daily_bonus' | 'reward_redeemed' | 'bonus';
    description: string;
    mediumName?: string;
}

export interface InteractionHistory {
    id: string;
    mediumId?: number | string;
    mediumName: string;
    mediumImage: string;
    date: string;
    duration: string;
    notes?: string;
    pointsEarned?: number;
    messages?: ChatMessage[];
}

export interface UserLocation {
    ip: string;
    country: string;
    city: string;
    flag: string;
    country_code: string;
}

// --- AI MEMORY STRUCTURES ---
export interface LearnedFact {
    category: 'vie_perso' | 'travail' | 'amour' | 'psychologie';
    fact: string;
    date: string;
}

export interface ClientMemory {
    phone: string;
    lastIp?: string; // Dernière adresse IP de connexion // Clé partielle
    mediumId: number; // Clé partielle (Isolation par médium)
    name: string;
    firstSeen: string;
    lastSeen: string;
    facts: LearnedFact[]; // Ce que CE médium a appris
    predictions: string[]; // Ce que CE médium a prédit
    interactionCount: number;
    emotionalProfile: string; // Ex: "Anxieux", "Serein", "Pressé"
    summary?: string; // Résumé global de la conversation
    concerns?: string; // Préoccupations majeures du client
    problem?: string; // Problème principal du client
    isBlocked?: boolean; // Si true, le médium refuse de parler à ce client
}

export interface FreeAstresBatch {
    id: string;
    amount: number;
    remaining: number;
    earnedAt: string; // ISO date
    expiresAt: string; // ISO date (earnedAt + 30 jours)
    source: 'welcome' | 'daily' | 'review' | 'bonus';
    description: string;
}

export interface PurchasedPack {
    id: string;
    packType: '20' | '50' | '100' | '150';
    name: string;
    price: number;
    baseAstres: number;
    bonusAstres: number;
    totalAstres: number;
    minutes?: number;
    purchaseDate: string;
    transcashCode?: string;
    status: 'active' | 'consumed';
}

export interface UnlockedRewardInfo {
    id: string;
    title: string;
    description: string;
    cost: number;
    icon: string;
    category?: string;
    badge?: string;
    unlockedAt: string;
}

export interface ClientUser {
    id: string;
    name: string;
    email: string;
    phone: string;
    lastIp?: string; // Dernière adresse IP de connexion     
    address: string; // Utilisé pour stocker la Ville/Cité
    gender: 'homme' | 'femme';
    birthDate?: string; // Date de naissance YYYY-MM-DD
    password: string; 
    createdAt: Date;
    avatar?: string;       
    history?: InteractionHistory[];
    location?: UserLocation; // Added Real Location
    loyaltyPoints?: number; // Total points/astres disponibles (freeAstres + paidAstres)
    freeAstres?: number; // Solde des astres gratuits/offerts non expirés (durée 30 jours)
    freeAstresBatches?: FreeAstresBatch[]; // Lots d'astres gratuits avec dates d'expiration à 30 jours
    paidAstres?: number; // Solde des astres achetés via Transcash (durée illimitée, n'expirent jamais)
    loyaltyTier?: LoyaltyTier; // Rang de fidélité (Bronze, Argent, Or, Platine, Diamant)
    totalSessionMinutes?: number; // Total des minutes de sessions accumulées
    consultationCreditsMinutes?: number; // Solde des minutes de forfaits achetés disponibles
    purchasedPacks?: PurchasedPack[]; // Historique des packs achetés
    claimedDailyBonusDate?: string; // Date (YYYY-MM-DD) du dernier bonus réclamé
    lastReviewBonusDate?: string; // Date (YYYY-MM-DD) de la dernière évaluation récompensée (+5 Astres, max 1x/jour)
    unlockedRewards?: string[]; // IDs des récompenses débloquées
    recentUnlockedReward?: UnlockedRewardInfo; // Dernière récompense débloquée avec badge
    loyaltyTransactions?: LoyaltyPointsTransaction[]; // Historique des gains/dépenses de points
    notes?: string; // Notes internes de l'admin
    tags?: string[]; // Tags de segmentation (ex: VIP, Nouveau, Litige...)
    lastActiveTime?: number; // Timestamp de dernière activité
    isSuspended?: boolean; // Compte suspendu par l'administrateur
    suspensionReason?: string; // Motif de suspension
    suspendedAt?: string; // Date de suspension ISO/FR
    adminMessages?: AdminMessage[]; // Messages envoyés par l'administration au client
    pushSubscription?: WebPushSubscription; // Inscription aux notifications Web Push distantes
    pushEnabled?: boolean; // Si le client a autorisé les pushs sur son appareil
    pushSubscribedAt?: string; // Date de souscription aux notifications push
}

export interface WebPushSubscription {
    endpoint: string;
    keys: {
        p256dh: string;
        auth: string;
    };
    userAgent?: string;
    subscribedAt?: string;
    clientId?: string;
    phone?: string;
}

export interface PushNotificationPayload {
    title: string;
    body: string;
    icon?: string;
    badge?: string;
    url?: string;
    tag?: string;
    mediumId?: number;
    mediumName?: string;
    data?: any;
}

export interface AdminMessage {
    id: string;
    title: string;
    message: string;
    date: string;
    read?: boolean;
    senderName?: string;
}

export interface GuestUser {
    name: string;
    email: string; // Added Mandatory Email
    phone: string;
    lastIp?: string; // Dernière adresse IP de connexion
    address: string; // Utilisé pour stocker la Ville/Cité
    gender: 'homme' | 'femme';
    birthDate?: string; // Date de naissance YYYY-MM-DD
    location?: UserLocation;
}

export enum ZodiacSign {
  Aries = "Bélier",
  Taurus = "Taureau",
  Gemini = "Gémeaux",
  Cancer = "Cancer",
  Leo = "Lion",
  Virgo = "Vierge",
  Libra = "Balance",
  Scorpio = "Scorpion",
  Sagittarius = "Sagittaire",
  Capricorn = "Capricorne",
  Aquarius = "Verseau",
  Pisces = "Poissons"
}

export interface ForumResponse {
    id?: string;
    author: string;
    text: string;
    date: string;
    createdAt?: string;
    isUser?: boolean;
    parentId?: string;
    replyToAuthor?: string;
}

export interface AuditLog {
    id: string;
    action: string;
    adminName: string;
    targetName?: string;
    details: string;
    timestamp: string;
    type: 'create' | 'update' | 'delete' | 'security' | 'financial' | 'message';
}

export interface ForumTopic {
    id: string;
    category: string;
    title: string;
    author: string;
    date: string;
    createdAt?: string;
    replies: number;
    views: number;
    content: string; // Le message principal
    responses: ForumResponse[]; // Les réponses
}

// --- CONFIGURATION DYNAMIQUE DES NOTIFICATIONS PROMOTIONNELLES ---
export type PromoPosition = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' | 'bottom-center';
export type PromoSize = 'compact' | 'normal' | 'large';
export type PromoTargetAudience = 'all' | 'guests_only' | 'registered_only';
export type PromoTemplateType = 'gold_gift_10min' | 'pack_vip_flash' | 'love_return' | 'tarot_instant' | 'horoscope_reveal' | 'custom';
export type PromoGradientTheme = 'gold' | 'purple' | 'emerald' | 'crimson' | 'night';

export interface PromoConfig {
    enabled: boolean;
    position: PromoPosition;
    size: PromoSize;
    targetAudience: PromoTargetAudience;
    template: PromoTemplateType;
    title: string;
    subtitle: string;
    badgeText: string;
    giftHighlight: string;
    ctaText: string;
    ctaAction: 'open_form' | 'open_premium' | 'open_sarah' | 'open_tarot';
    imageUrl?: string;
    displayDelaySeconds: number; // Ex: 10s, 30s
    repeatIntervalMinutes: number; // Ex: 5min, 15min
    startMinimized: boolean; // Si true, commence en petite bulle
    gradientTheme: PromoGradientTheme;
}

export interface DreamAnalysisResult {
    title: string;
    analysis: string;
    symbols: { name: string; meaning: string }[];
    advice: string;
    warning: string;
    prediction: string;
}


